import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "@/lib/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", service: "", message: "", website: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thanks! Your enquiry has been sent. We'll be in touch soon.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please email info@stotttech.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <span className="font-heading font-bold uppercase text-sm tracking-widest text-[#E60000]">
            Get in touch
          </span>
          <h2 className="font-heading font-black uppercase tracking-tight text-3xl md:text-5xl mt-3 mb-6">
            Tell us what you need
          </h2>
          <p className="text-lg text-black/70 leading-relaxed mb-8">
            Send us a message about your IT challenge and we'll get back to you.
            No jargon, no pressure — just honest advice and a clear next step.
          </p>
          <a
            href="mailto:info@stotttech.com"
            data-testid="contact-email-link"
            className="inline-flex items-center gap-3 border-2 border-black px-5 py-4 font-heading font-bold hover:bg-black hover:text-white transition-colors"
          >
            <Mail className="h-5 w-5 text-[#E60000]" />
            info@stotttech.com
          </a>
        </div>

        <form
          onSubmit={submit}
          data-testid="enquiry-form"
          className="border-2 border-black p-6 sm:p-8 bg-[#F4F4F5]"
        >
          {/* Honeypot: hidden from users, bots tend to fill it */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" style={{ opacity: 0 }}>
            <label>
              Website (leave blank)
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
              />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-heading font-bold uppercase text-xs tracking-widest">Name *</Label>
              <Input
                data-testid="form-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Your name"
                className="mt-2 rounded-none border-2 border-black bg-white focus-visible:ring-1 focus-visible:ring-[#E60000] focus-visible:border-[#E60000]"
              />
            </div>
            <div>
              <Label className="font-heading font-bold uppercase text-xs tracking-widest">Email *</Label>
              <Input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@company.co.uk"
                className="mt-2 rounded-none border-2 border-black bg-white focus-visible:ring-1 focus-visible:ring-[#E60000] focus-visible:border-[#E60000]"
              />
            </div>
          </div>

          <div className="mt-5">
            <Label className="font-heading font-bold uppercase text-xs tracking-widest">Company</Label>
            <Input
              data-testid="form-company"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              placeholder="Optional"
              className="mt-2 rounded-none border-2 border-black bg-white focus-visible:ring-1 focus-visible:ring-[#E60000] focus-visible:border-[#E60000]"
            />
          </div>

          <div className="mt-5">
            <Label className="font-heading font-bold uppercase text-xs tracking-widest">Service *</Label>
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger
                data-testid="form-service"
                className="mt-2 rounded-none border-2 border-black bg-white focus:ring-1 focus:ring-[#E60000]"
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-2 border-black">
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} className="rounded-none">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-5">
            <Label className="font-heading font-bold uppercase text-xs tracking-widest">Message *</Label>
            <Textarea
              data-testid="form-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="How can we help?"
              rows={5}
              className="mt-2 rounded-none border-2 border-black bg-white focus-visible:ring-1 focus-visible:ring-[#E60000] focus-visible:border-[#E60000]"
            />
          </div>

          <button
            type="submit"
            data-testid="form-submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E60000] text-white font-heading font-bold uppercase tracking-wide px-8 py-4 border-2 border-black hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000] transition-all disabled:opacity-60 disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send enquiry <Send className="h-5 w-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};
